import { Injectable, computed, inject, signal } from '@angular/core';
import { CartItem, CartItemAssignment } from '@shared/models/cart.model';
import { MenuProductResponse } from '../models/response/menu-response.model';
import { AuthStateManager } from '@features/auth/services/auth-state-manager-service';

import { logger } from '@core/utils/logger';
@Injectable({ providedIn: 'root' })
export class CartService {
  private authState = inject(AuthStateManager);

  private _items = signal<CartItem[]>(this.loadFromStorage());

  items = this._items.asReadonly();

  total = computed(() =>
    this._items().reduce((sum, item) => sum + item.productPrice * item.quantity, 0),
  );

  itemCount = computed(() => this._items().reduce((sum, item) => sum + item.quantity, 0));

  addItem(
    product: MenuProductResponse,
    quantity: number,
    specialInstructions: string | null,
    assignments: CartItemAssignment[] = [],
  ) {
    const instructions = product.customizable ? specialInstructions : null;

    this.addItemInternal(
      product.publicId,
      product.name,
      product.price,
      instructions,
      quantity,
      product.imageUrl ?? undefined,
      product.customizable,
      assignments,
    );
  }

  private addItemInternal(
    productId: string,
    productName: string,
    productPrice: number,
    specialInstructions: string | null,
    quantity: number,
    productImage?: string,
    customizable = false,
    assignments: CartItemAssignment[] = [],
  ): void {
    const currentItems = this._items();
    const newItemKey = this.buildCartItemKey(productId, specialInstructions, assignments);

    const existingIndex = currentItems.findIndex(
      (item) =>
        this.buildCartItemKey(
          item.productId,
          item.specialInstructions ?? null,
          item.assignments ?? [],
        ) === newItemKey,
    );

    if (existingIndex >= 0) {
      const updated = [...currentItems];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: updated[existingIndex].quantity + quantity,
      };
      this._items.set(updated);
    } else {
      this._items.update((items) => [
        ...items,
        {
          productId,
          productName,
          productPrice,
          productImage,
          quantity: quantity,
          specialInstructions,
          customizable,
          assignments,
        },
      ]);
    }
    this.saveToStorage();
  }

  updateQuantity(index: number, quantity: number): void {
    if (quantity <= 0) {
      this.removeItem(index);
      return;
    }

    this._items.update((items) => {
      const updated = [...items];
      updated[index] = { ...updated[index], quantity };
      return updated;
    });

    this.saveToStorage();
  }

  updateInstructions(index: number, instructions: string): void {
    this._items.update((items) => {
      const updated = [...items];
      updated[index] = { ...updated[index], specialInstructions: instructions };
      return updated;
    });

    this.saveToStorage();
  }

  removeItem(index: number): void {
    this._items.update((items) => items.filter((_, i) => i !== index));
    this.saveToStorage();
  }

  clear(): void {
    this._items.set([]);
    localStorage.removeItem('cart');
  }

  private saveToStorage(): void {
    try {
      const currentVenueId = this.authState.foodVenueId();

      const payload = {
        venueId: currentVenueId,
        items: this._items(),
      };

      localStorage.setItem('cart', JSON.stringify(payload));
    } catch (error) {
      logger.error('Pedidos', 'No se pudo guardar el carrito', error);
    }
  }

  private loadFromStorage(): CartItem[] {
    try {
      const raw = localStorage.getItem('cart');
      if (!raw) return [];

      const parsed = JSON.parse(raw);

      const currentVenueId = this.authState.foodVenueId();

      const storedItems = Array.isArray(parsed) ? parsed : parsed.items || [];
      const storedVenueId = Array.isArray(parsed) ? null : parsed.venueId;

      if (currentVenueId && storedVenueId && currentVenueId !== storedVenueId) {
        logger.warn(
          'Pedidos',
          `El carrito guardado es de otro local: se vacía, local=${storedVenueId}`,
        );
        localStorage.removeItem('cart');
        return [];
      }

      return storedItems;
    } catch (error) {
      logger.error('Pedidos', 'No se pudo leer el carrito guardado', error);
      return [];
    }
  }

  private normalizeAssignments(assignments?: CartItemAssignment[]) {
    return (assignments ?? [])
      .map((a) => ({
        participantId: a.participantId,
        assignedAmount: a.assignedAmount ?? null,
        assignedPercentage: a.assignedPercentage ?? null,
        assignedQuantity: a.assignedQuantity ?? null,
      }))
      .sort((a, b) => a.participantId.localeCompare(b.participantId));
  }

  private buildCartItemKey(
    productId: string,
    specialInstructions: string | null,
    assignments?: CartItemAssignment[],
  ): string {
    const normalizedInstructions = specialInstructions?.trim().toLowerCase() || '';
    const normalizedAssignments = this.normalizeAssignments(assignments);

    return JSON.stringify({
      productId,
      specialInstructions: normalizedInstructions,
      assignments: normalizedAssignments,
    });
  }
}
